import { PanelToggleButton } from '@heidi/design-system/panel-toggle-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { cn } from '@heidi/utils';
import { Menu, SquarePen } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ProjectDetail } from '../../../contracts/projects.types';
import { projectPagesMessages } from '../../../data/projects/projectPages.messages';
import { CreatedChatArtifactsButton } from './CreatedChatArtifactsButton';
import { HeaderIconButton } from './HeaderIconButton';
import { ProjectChatMenu, ProjectChatMenuItems } from './ProjectChatMenu';
import type {
  ProjectHeaderActionsProps,
  ProjectHeaderCommonMenuItemsProps,
} from './ProjectHeaderActions';
import type { ProjectTitleClusterProps } from './ProjectTitleCluster';

export interface ProjectChatHeaderProps {
  /** Active project, or null/undefined while metadata is still loading. */
  project: ProjectDetail | null | undefined;
  projectId: string | undefined;
  isEditingTitle: boolean;
  onEditingTitleChange: (editing: boolean) => void;
  isChatSidebarOpen: boolean;
  onOpenSidebar: () => void;
  showMakeAsWorkflow: boolean;
  onMakeAsWorkflow: () => void;
  readOnly?: boolean;
  showShare?: boolean;
  showDuplicate?: boolean;
  onShare?: () => void;
  onDuplicate?: () => void;
  /** The same action as the sidebar's New chat row. */
  onNewChat?: () => void;
  mobileActions?: {
    onOpenNavigation: () => void;
    onOpenHistory: () => void;
  };
}

/** The chat chrome a routine-run header lays out beside its own controls. */
export interface ProjectChatHeaderRoutineChrome {
  compact: boolean;
  sidebarControl: ReactNode;
  headerActions: ReactNode;
  additionalMenuItems: ReactNode;
}

/**
 * What the app supplies: the mobile header's copy, the title cluster and the two
 * action menus bound to its chat-list mutations, and — only when a routine run
 * backs this chat — that routine's own header. Pass stable components.
 */
export interface ProjectChatHeaderHost {
  /** The New chat label, shared with the app's mobile header. */
  newChatLabel: string;
  /** The navigation-menu label, shared with the app's mobile header. */
  navigationLabel: string;
  TitleCluster: ComponentType<ProjectTitleClusterProps>;
  HeaderActions: ComponentType<ProjectHeaderActionsProps>;
  CommonMenuItems: ComponentType<ProjectHeaderCommonMenuItemsProps>;
  /** Present when a routine run backs this chat; it renders that routine's header. */
  renderRoutineRunHeader?: (chrome: ProjectChatHeaderRoutineChrome) => ReactNode;
}

/**
 * Header for the project chat page.
 *
 * Two shapes behind one component because the difference is data, not layout:
 * when a routine run backs this chat the routine's own controls replace the
 * chat title + project actions (`renderRoutineRunHeader`). Plain projects
 * keep the title cluster + overflow actions.
 *
 * The header shell is full-bleed across the chat column; the transcript and
 * composer below stay on the narrower `max-w-3xl` track.
 */
export const ProjectChatHeader = ({
  project,
  projectId,
  isEditingTitle,
  onEditingTitleChange,
  isChatSidebarOpen,
  onOpenSidebar,
  showMakeAsWorkflow,
  onMakeAsWorkflow,
  readOnly = false,
  showShare = false,
  showDuplicate = false,
  onShare,
  onDuplicate,
  onNewChat,
  mobileActions,
  newChatLabel,
  navigationLabel,
  TitleCluster,
  HeaderActions,
  CommonMenuItems,
  renderRoutineRunHeader,
}: ProjectChatHeaderProps & ProjectChatHeaderHost) => {
  const intl = useIntl();

  const sidebarLabel = intl.formatMessage(projectPagesMessages.openSidebar);

  const newChatButton = onNewChat ? (
    <HeaderIconButton label={newChatLabel} icon={SquarePen} onClick={onNewChat} />
  ) : null;
  const createdArtifactsButton = projectId ? (
    <CreatedChatArtifactsButton projectId={projectId} />
  ) : null;
  // A leading desktop toggle sits 8px from the card's start edge, matching the header's pt-2.
  const showsDesktopToggle = !mobileActions && !isChatSidebarOpen;
  const rowInsetClassName = mobileActions
    ? 'px-3'
    : showsDesktopToggle
      ? 'ps-2 pe-6'
      : 'px-6';
  const sidebarToggle = mobileActions ? (
    <HeaderIconButton
      label={navigationLabel}
      icon={Menu}
      onClick={mobileActions.onOpenNavigation}
    />
  ) : showsDesktopToggle ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <PanelToggleButton
          open={false}
          side="left"
          variant="ghost"
          size="sm"
          className="text-text-primary"
          onClick={onOpenSidebar}
          aria-label={sidebarLabel}
        />
      </TooltipTrigger>
      <TooltipContent side="bottom">{sidebarLabel}</TooltipContent>
    </Tooltip>
  ) : null;

  if (renderRoutineRunHeader) {
    return (
      <header className="shrink-0 pb-4 pt-2">
        <div className={cn('w-full', rowInsetClassName)}>
          <div className="flex items-center justify-between gap-1">
            <div className="min-w-0 flex-1">
              {renderRoutineRunHeader({
                compact: !!mobileActions,
                sidebarControl: sidebarToggle,
                headerActions: (
                  <>
                    {newChatButton}
                    {createdArtifactsButton}
                  </>
                ),
                additionalMenuItems:
                  project && projectId ? (
                    <CommonMenuItems
                      project={project}
                      projectId={projectId}
                      readOnly={readOnly}
                      showShare={showShare}
                      showDuplicate={showDuplicate}
                      onShare={onShare}
                      onDuplicate={onDuplicate}
                      onOpenHistory={mobileActions?.onOpenHistory}
                    />
                  ) : mobileActions ? (
                    <ProjectChatMenuItems onOpenHistory={mobileActions.onOpenHistory} />
                  ) : undefined,
              })}
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="shrink-0 pb-4 pt-2">
      <div className={cn('w-full', rowInsetClassName)}>
        <div className="flex items-center justify-between gap-1 sm:gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {sidebarToggle}
            <TitleCluster
              project={project}
              projectId={projectId}
              isEditing={isEditingTitle}
              onEditingChange={onEditingTitleChange}
            />
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {newChatButton}
            {createdArtifactsButton}
            {project && projectId ? (
              <HeaderActions
                project={project}
                projectId={projectId}
                onRename={() => onEditingTitleChange(true)}
                showMakeAsWorkflow={showMakeAsWorkflow}
                onMakeAsWorkflow={onMakeAsWorkflow}
                readOnly={readOnly}
                showShare={showShare}
                showDuplicate={showDuplicate}
                onShare={onShare}
                onDuplicate={onDuplicate}
                onOpenHistory={mobileActions?.onOpenHistory}
              />
            ) : mobileActions ? (
              <ProjectChatMenu onOpenHistory={mobileActions.onOpenHistory} />
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
};
